// ──────────────────────────────────────────────────────────────────────
// Event Personality System — Phase 2A
// Presentation-layer mapping from event_type → UI copy + CSS theme class.
// Purely additive; zero impact on API shape, DB schema, or business logic.
// ──────────────────────────────────────────────────────────────────────

export const EVENT_PERSONALITIES = {
  private_experience: {
    name: 'Private Experience',
    themeClass: 'event-private',
    labels: {
      ticket: 'Access Pass',
      tickets: 'Access Passes',
      guests: 'Approved Guests',
      ctaPrimary: 'Request Access',
      ctaSecondary: 'View Details',
      capacity: 'Intimate Capacity',
      location: 'Venue',
    },
    helper: {
      access: 'This experience is by approval only. Request access to continue.',
      location: 'Full address will be shared with approved guests.',
      tiers: 'Select your access level.',
    },
  },
  curated_gathering: {
    name: 'Curated Gathering',
    themeClass: 'event-curated',
    labels: {
      ticket: 'Invitation',
      tickets: 'Invitations',
      guests: 'Curated Guests',
      ctaPrimary: 'Claim Invitation',
      ctaSecondary: 'Learn More',
      capacity: 'Guest List',
      location: 'Venue',
    },
    helper: {
      access: 'Thoughtfully curated — your invitation connects you to this evening.',
      location: 'Venue details shared with confirmed guests.',
      tiers: 'Choose your invitation style.',
    },
  },
  house_party: {
    name: 'House Party',
    themeClass: 'event-house',
    labels: {
      ticket: 'Pass',
      tickets: 'Passes',
      guests: 'Guests',
      ctaPrimary: 'Grab My Pass',
      ctaSecondary: 'See Details',
      capacity: 'House Capacity',
      location: 'Address',
    },
    helper: {
      access: 'Grab your pass — see you on the floor.',
      location: 'Address shared with confirmed guests.',
      tiers: 'Pick your vibe.',
    },
  },
  club_venue: {
    name: 'Club / Venue',
    themeClass: 'event-club',
    labels: {
      ticket: 'Entry',
      tickets: 'Entries',
      guests: 'Guestlist',
      ctaPrimary: 'Get Entry',
      ctaSecondary: 'Event Info',
      capacity: 'Venue Capacity',
      location: 'Venue',
    },
    helper: {
      access: 'Secure your entry to the night.',
      location: 'Venue address below.',
      tiers: 'Select your entry level.',
    },
  },
  sporting_event: {
    name: 'Sporting Event',
    themeClass: 'event-sporting',
    labels: {
      ticket: 'Ticket',
      tickets: 'Tickets',
      guests: 'Attendees',
      ctaPrimary: 'Get Tickets',
      ctaSecondary: 'Match Details',
      capacity: 'Stadium Capacity',
      location: 'Venue',
    },
    helper: {
      access: "Lock in your seat — it's almost game time.",
      location: 'Check in at the venue entrance.',
      tiers: 'Choose your section.',
    },
  },
  custom: {
    name: 'Custom',
    themeClass: 'event-custom',
    labels: {
      ticket: 'Pass',
      tickets: 'Passes',
      guests: 'Guests',
      ctaPrimary: 'Get My Pass',
      ctaSecondary: 'Learn More',
      capacity: 'Capacity',
      location: 'Location',
    },
    helper: {
      access: 'Reserve your pass below.',
      location: 'See location details below.',
      tiers: 'Select your entry.',
    },
  },
};

// Accept both the human-readable form ("Private Experience") stored by the
// guided builder AND the snake_case key form ("private_experience"). Unknown
// or falsy types fall back to `custom`.
const TYPE_ALIASES = {
  'private experience': 'private_experience',
  'curated gathering': 'curated_gathering',
  'house party': 'house_party',
  'club / venue': 'club_venue',
  'club/venue': 'club_venue',
  'club venue': 'club_venue',
  'sporting event': 'sporting_event',
  'sports': 'sporting_event',
  'custom': 'custom',
  'other': 'custom',
};

export function getEventPersonality(eventType) {
  if (!eventType || typeof eventType !== 'string') {
    return EVENT_PERSONALITIES.custom;
  }
  const normalized = eventType.trim().toLowerCase();
  // Direct key match (snake_case input)
  if (EVENT_PERSONALITIES[normalized]) return EVENT_PERSONALITIES[normalized];
  // Human-readable alias match
  const aliased = TYPE_ALIASES[normalized];
  if (aliased && EVENT_PERSONALITIES[aliased]) return EVENT_PERSONALITIES[aliased];
  return EVENT_PERSONALITIES.custom;
}
